import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import {
  AbstractMouseEvent,
  Action,
  Button,
  CGSKeyboardEvent,
  Container,
  easeOutCubicInterpolate,
  EmptyAction,
  FunctionAction,
  getAnimationDuration,
  InterpolateCopyAction,
  lerp,
  MouseDownEvent,
  ParallelSimpleAction,
  Rect,
  SceneObject,
  SequenceSimpleAction,
  SoundSceneObject,
  TextSceneObject,
  Vector2,
} from '@cgs/wge';
import { RESOURCES_COMPONENT_TOKEN } from '../../../../type_definitions';
import { EpicWinConfiguration } from '../../../components/epic_win/epic_win_configuration';
import { EpicWinStep, EpicWinStepName } from '../../../components/epic_win/epic_win_step';
import { ResourcesComponent } from '../../../components/resources_component';
import { SLOT_SESSION_TOKEN, SlotSession } from '../../slot_session';
import { EpicWinPopupController } from '../controllers/epic_win_popup_controller';
import { BaseSlotPopupView, SlotPopups } from './base_popup_view';
import { Key } from 'ts-keycode-enum';

export class EpicWinPopupView extends BaseSlotPopupView<EpicWinPopupController> {
  protected _popupNode: SceneObject;
  protected _configuration: EpicWinConfiguration;
  protected _winActionsOneByOne: Action[] = [];
  protected _allwinAction: Action;
  protected _currentStep: number;
  protected _slotSession: SlotSession;
  protected _cashLoopSoundScene: SoundSceneObject | null;

  private readonly _textNodes: SceneObject[];
  protected readonly _closeBtn: Button;
  protected readonly numberFormatter: INumberFormatter;

  constructor(
    container: Container,
    root: SceneObject,
    popup: SceneObject,
    soundNode: SceneObject | null,
    configuration: EpicWinConfiguration
  ) {
    super(root, popup, soundNode, SlotPopups.EpicWin);
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);

    this._slotSession = container.resolve<SlotSession>(SLOT_SESSION_TOKEN);
    this._popupNode = this.view.findById('popup')!;
    this._popupNode.touchable = false;
    this._closeBtn = this.view.findById<Button>('closeBtn')!;
    this._closeBtn.touchable = false;

    this._currentStep = 0;
    //TODO: remove hard-code (config haven't got size!)
    this._popupNode.touchArea = new Rect(
      new Vector2(-1000.0, -1000.0),
      new Vector2(10000.0, 10000.0)
    );
    if (!this._closeBtn) {
      this._popupNode.eventReceived.listen((e) => {
        if (e instanceof AbstractMouseEvent) {
          e.accept();
          if (e instanceof MouseDownEvent) {
            this.hide();
          }
        }
      });
    } else {
      this._closeBtn.clicked.listen(() => {
        this.hide();
      });
    }

    if (configuration.closeBySpace) {
      this._popupNode.eventReceived.listen((e) => {
        if (e instanceof CGSKeyboardEvent && e.keyCode === Key.Space) {
          this.closeBySpace(e);
        }
      });
    }

    this._soundNode = soundNode;
    this._configuration = configuration;
    this._textNodes = this._popupNode.findAllById('text');
    this.view.findAllById<TextSceneObject>('result').forEach((textScene) => (textScene.text = '0'));

    const resourceComponent: ResourcesComponent =
      container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    this._cashLoopSoundScene = resourceComponent.sounds.findById('cash_loop');
  }

  public closeBySpace(e: CGSKeyboardEvent): void {
    this.hide();
    e.accept();
  }

  public hide(): void {
    if (this._winActionsOneByOne.length > this._currentStep) {
      this._allwinAction.end();
    } else {
      this._popupNode.stateMachine?.switchToState('empty');
      const texts = this.view.findAllById<TextSceneObject>('result');
      texts.forEach((e) => (e.text = '0'));
      super.hide();
    }
  }

  public setWinAmount(winAmount: number): void {
    this.view
      .findAllById<TextSceneObject>('result')
      .forEach((textScene) => (textScene.text = this.numberFormatter.formatMoney(winAmount)));
  }

  public showWinAction(winName: EpicWinStepName, totalWin: number): Action {
    this._popupNode.touchable = false;
    this._closeBtn.touchable = false;

    const epicWinStep = this._configuration.steps.find((s) => s.winName == winName)!;

    const animationActions: Action[] = [];
    this._currentStep = 0;
    this.addActions(epicWinStep, winName, animationActions, totalWin);

    animationActions.push(
      new ParallelSimpleAction([
        new FunctionAction(() => this.endTextAnimation()),
        new EmptyAction().withDuration(
          this._configuration.finishTextAnimationDelay + this._configuration.showCounterDelay
        ),
      ])
    );
    animationActions.push(
      new ParallelSimpleAction([
        new FunctionAction(() => this.hideWin(winName)),
        new EmptyAction().withDuration(this._configuration.closePopupDelay),
      ])
    );
    animationActions.push(new FunctionAction(() => new Promise(() => this.hide())));

    const sequence = new SequenceSimpleAction(animationActions);

    return new ParallelSimpleAction([
      new SequenceSimpleAction([
        new EmptyAction().withDuration(1),
        new FunctionAction(() => {
          this._popupNode.touchable = true;
          this._closeBtn.touchable = true;
        }),
      ]),
      sequence,
      new SequenceSimpleAction([
        new EmptyAction().withDuration(this._configuration.showCounterDelay),
        new FunctionAction(() => this._soundNode?.stateMachine?.switchToState('coins')),
        new FunctionAction(() => this._soundNode?.stateMachine?.switchToState('coins')),
        new FunctionAction(() => this._soundNode?.stateMachine?.switchToState('empty')),
      ]),
    ]);
  }

  protected addActions(
    step: EpicWinStep,
    winName: EpicWinStepName,
    actions: Action[],
    totalWin: number
  ): void {
    if (!this._configuration.steps.find((s) => s.winName === winName)) {
      return;
    }

    if (step.priority === 1) {
      actions.push(new FunctionAction(() => this.startTextAnimation()));
    }

    const stepCount = this._configuration.steps.findIndex((item) => item.winName === winName);

    if (step.skipTransitions) {
      const incrementWinAction = this.interpolateTextAction(0, totalWin);
      this._winActionsOneByOne.push(
        new ParallelSimpleAction([
          incrementWinAction,
          new SequenceSimpleAction([
            new FunctionAction(() =>
              this._popupNode.stateMachine!.switchToState(`step_${stepCount}`)
            ),
            new EmptyAction().withDuration(step.animationDuration + 0.2),
            this.lastStepsShownAction(),
            new FunctionAction(() => {
              this._currentStep = stepCount + 1;
            }),
          ]),
        ])
      );
    } else {
      const incrementFirstWinAction = this.interpolateTextAction(
        0,
        Math.min(
          totalWin,
          this.getTotalBet() * this._configuration.steps[0].finishesWithTotalBetCount
        )
      );
      this._winActionsOneByOne.push(
        new ParallelSimpleAction([
          incrementFirstWinAction,
          new SequenceSimpleAction([
            new FunctionAction(() => this._popupNode.stateMachine!.switchToState('step_0')),
            new EmptyAction().withDuration(this._configuration.steps[0].animationDuration + 0.2),
            stepCount === 0 ? this.lastStepsShownAction() : new EmptyAction(),
            new FunctionAction(() => {
              this._currentStep++;
            }),
          ]),
        ])
      );

      for (let i = 0; i < stepCount; i++) {
        const incrementWinAction = this.interpolateTextAction(
          this.getTotalBet() * this._configuration.steps[i + 1].startFromTotalBetCount,
          Math.min(
            totalWin,
            this.getTotalBet() * this._configuration.steps[i + 1].finishesWithTotalBetCount
          )
        );

        this._winActionsOneByOne.push(
          new ParallelSimpleAction([
            new FunctionAction(() => {
              this._popupNode.stateMachine!.switchToState('next');
            }),
            incrementWinAction,
            new SequenceSimpleAction([
              new EmptyAction().withDuration(
                this._configuration.steps[i + 1].animationDuration + 0.2
              ),
              i === stepCount - 1 ? this.lastStepsShownAction() : new EmptyAction(),
              new FunctionAction(() => {
                this._currentStep++;
              }),
            ]),
          ])
        );
      }
    }
    this._allwinAction = new SequenceSimpleAction(this._winActionsOneByOne);
    actions.push(this._allwinAction);
    actions.push(
      new ParallelSimpleAction([
        new FunctionAction(() => {
          const state =
            this._currentStep - 1 === 0 ? 'static' : `step_${this._currentStep - 1}_static`;

          this._popupNode.stateMachine!.switchToState(state);
        }),
      ])
    );
    actions.push(new EmptyAction().withDuration(1.5));
    actions.push(
      new SequenceSimpleAction([
        new FunctionAction(() => {
          this._popupNode.stateMachine!.switchToState('hide');
          this._winActionsOneByOne.length = 0;
        }),
        new EmptyAction().withDuration(
          getAnimationDuration(this._popupNode, `step_${stepCount}_hide`)
        ),
      ])
    );
  }

  protected hideWin(winName: string): void {
    this._popupNode.stateMachine!.switchToState(winName + '_hide');
  }

  protected startTextAnimation(): void {
    for (const textNode of this._textNodes) {
      textNode.stateMachine!.switchToState('textanim');
    }
  }

  protected endTextAnimation(): void {
    for (const textNode of this._textNodes) {
      textNode.stateMachine!.switchToState('endanim');
    }
  }

  protected interpolateTextAction(startValue: number, totalWin: number): Action {
    const start0 = startValue;
    const start1 = 0.1 * (totalWin - startValue) + startValue;
    const start2 = 0.85 * (totalWin - startValue) + startValue;
    const firstAction = new InterpolateCopyAction<number>().withInterpolateFunction(lerp);
    firstAction.withDuration(1.0).withValues(start0, start1);
    firstAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    firstAction.beginning.listen(() => {
      this._cashLoopSoundScene?.stateMachine?.switchToState('stop');
      this._cashLoopSoundScene?.stateMachine?.switchToState('play');
    });
    const secondAction = new InterpolateCopyAction<number>().withInterpolateFunction(lerp);
    secondAction.withDuration(2.0).withValues(start1, start2);
    secondAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    const thirdAction = new InterpolateCopyAction<number>().withInterpolateFunction(
      easeOutCubicInterpolate
    );
    thirdAction.withDuration(2.0).withValues(start2, totalWin);
    thirdAction.valueChange.listen((win) => {
      this.setWinAmount(win);
    });
    thirdAction.done.listen(() => {
      this._cashLoopSoundScene?.stateMachine?.switchToState('stop');
    });
    return new SequenceSimpleAction([firstAction, secondAction, thirdAction]);
  }

  protected getTotalBet(): number {
    return this._slotSession.totalBet;
  }

  protected lastStepsShownAction(): Action {
    return new EmptyAction();
  }
}
